import { AnalyticsContextType } from '~/lib/contexts/AnalyticsContext';
import { DatadogContextType } from '~/lib/types/datadog/datadog';

import { ImageCompressionField, ImageEvents } from '~/analytics/imageEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

import { compressSMTImage } from '~/components/maud/patients/profile/smt/imageCompression.util';

type ImageCompressionEventProperties = {
  [ImageCompressionField.CompressedMediaType]: string;
  [ImageCompressionField.CompressedSizeKB]: number;
  [ImageCompressionField.CompressionDurationMs]: number;
  [ImageCompressionField.CompressionRate]: number;
  [ImageCompressionField.OriginalMediaType]: string;
  [ImageCompressionField.OriginalSizeKB]: number;
  [ImageCompressionField.Source]: string;
};

export type CompressionErrorContext = {
  action: 'addSMTEntry' | 'editSMTEntry';
  context: string;
  entryId?: string;
  patientId: string;
};

interface CompressImageWithAnalyticsParams {
  errorContext: CompressionErrorContext;
  imageFile: File;
  logError: DatadogContextType['logError'];
  source: AnalyticsSourceValues;
  trackEvent: AnalyticsContextType['trackEvent'];
}

/**
 * Compresses an image file and tracks analytics if compression succeeds.
 * Returns the compressed file, or the original file if compression fails.
 *
 * @param params - Configuration object containing image file, tracking functions, and error context
 * @returns Promise resolving to the file to use (compressed or original)
 */
export const compressImageWithAnalytics = async ({
  imageFile,
  trackEvent,
  logError,
  errorContext,
  source,
}: CompressImageWithAnalyticsParams): Promise<File> => {
  try {
    const compressionResult = await compressSMTImage(imageFile);

    if (compressionResult.metadata.wasCompressed) {
      const eventProperties: ImageCompressionEventProperties = {
        [ImageCompressionField.CompressedMediaType]:
          compressionResult.metadata.compressedMediaType,
        [ImageCompressionField.CompressedSizeKB]:
          compressionResult.metadata.compressedSizeKB,
        [ImageCompressionField.CompressionDurationMs]:
          compressionResult.metadata.compressionDurationMs,
        [ImageCompressionField.CompressionRate]:
          compressionResult.metadata.compressionRate,
        [ImageCompressionField.OriginalMediaType]:
          compressionResult.metadata.originalMediaType,
        [ImageCompressionField.OriginalSizeKB]:
          compressionResult.metadata.originalSizeKB,
        [ImageCompressionField.Source]: source,
      };
      trackEvent(ImageEvents.imageCompressed, eventProperties);
    }

    return compressionResult.file;
  } catch (compressionError) {
    logError(compressionError, errorContext);
    return imageFile;
  }
};
